import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient, useQuery } from '@tanstack/react-query';
import {
  useGetAvailableAppointmentSlots,
  useCreateBooking,
  useCancelBooking,
  getUserBookings,
  getGetUserBookingsQueryKey,
} from '../../../common/api/endpoints/appointments/appointments';
import { useGetExamSessionStatus } from '../../../common/api/endpoints/exam-sessions/exam-sessions';
import { useRefetchModuleTasks } from '../../../common/hooks/student/useRefetchModuleTasks';
import { useAuthStore } from '../../../common/auth/store';
import log from '../../../common/utils/logger';
import { AppointmentSlot } from '../../../common/api/models/appointmentSlot';
import { Booking } from '../../../common/api/models/booking';
import { isLiveKitSlot } from '../../../common/utils/oralExam/liveKitSlot';
import { createLiveKitJoinHandle } from '../../../common/utils/oralExam/liveKitJoin';
import type { OralExamTokenResponse } from '../../../common/api/models/oralExamTokenResponse';
import type { Task } from '../../types';
import { ORAL_EXAM_PRACTICE_KIND_ID } from '../../../common/utils/oralExam/practiceApi';
import { useDialog } from '../../../common/context/DialogContext';
import {
  guardStudentAction,
  STUDENT_PREVIEW_SAMPLE_SLOTS,
  useStudentPreviewBookingState,
  useStudentPreviewSource,
} from '../../../common/studentPreview/StudentPreviewSource';

export type LiveKitJoinHandle = {
  provider: 'livekit';
  bookingId?: string;
  /** Lecturer slot-scoped join (no booking); used for token cache keys. */
  slotId?: string;
  requestToken: () => Promise<OralExamTokenResponse>;
};

// Lower number wins when picking which booking represents "the" current booking
// for a task. An active booking must always outrank a completed one, otherwise
// the UI offers "Register for Another Session" and the next POST /bookings 400s.
const BOOKING_STATUS_PRIORITY: Record<string, number> = {
  pending: 0,
  confirmed: 1,
  completed: 2,
};

/**
 * Pick the most relevant booking for a given task from a student's booking list.
 *
 * - Filters out cancelled bookings.
 * - Filters out bookings for other tasks.
 * - Prefers pending > confirmed > completed (active beats archived).
 * - Ties are broken by most recent startTime first.
 */
type OralExamAnchorTask = Pick<Task, 'type' | 'eventConfig' | 'oralExamConfig'> | undefined;

/**
 * For official oral exams, the server pool pin sets registeredExamAt when pinned.
 * null means the pool is open (ignore completed sittings). undefined leaves legacy
 * selection unchanged (group study, practice, and non-event tasks).
 */
export const oralExamCompletedBookingAnchor = (
  task: OralExamAnchorTask,
): string | null | undefined => {
  if (!task || task.type !== 'oral_exam') return undefined;
  const kindId = task.eventConfig?.eventKindId;
  if (kindId === ORAL_EXAM_PRACTICE_KIND_ID) return undefined;
  return task.oralExamConfig?.registeredExamAt ?? null;
};

export const selectCurrentBookingForTask = (
  bookings: Booking[],
  taskId: string | undefined,
  completedBookingAnchor?: string | null,
): Booking | undefined => {
  if (!taskId) return undefined;
  const candidates = bookings.filter((b) => {
    if (b.taskId !== taskId || b.status === 'cancelled') return false;
    if (completedBookingAnchor === undefined) return true;
    if (b.status !== 'completed') return true;
    if (completedBookingAnchor === null) return false;
    return b.startTime === completedBookingAnchor;
  });
  if (candidates.length <= 1) return candidates[0];
  return [...candidates].sort((a, b) => {
    const byStatus = (BOOKING_STATUS_PRIORITY[a.status] ?? 99) - (BOOKING_STATUS_PRIORITY[b.status] ?? 99);
    if (byStatus !== 0) return byStatus;
    return new Date(b.startTime).getTime() - new Date(a.startTime).getTime();
  })[0];
};

/**
 * Pick the most relevant booking for a given slot from a student's booking list.
 *
 * - Filters out cancelled bookings.
 * - Filters out bookings for other slots.
 * - Prefers pending > confirmed > completed (active beats archived).
 * - Ties are broken by most recent startTime first.
 */
export const selectCurrentBookingForSlot = (
  bookings: Booking[],
  slotId: string | undefined,
): Booking | undefined => {
  if (!slotId) return undefined;
  const candidates = bookings.filter((b) => b.slotId === slotId && b.status !== 'cancelled');
  if (candidates.length <= 1) return candidates[0];
  return [...candidates].sort((a, b) => {
    const byStatus = (BOOKING_STATUS_PRIORITY[a.status] ?? 99) - (BOOKING_STATUS_PRIORITY[b.status] ?? 99);
    if (byStatus !== 0) return byStatus;
    return new Date(b.startTime).getTime() - new Date(a.startTime).getTime();
  })[0];
};

/**
 * SlotMeetingScreen resolver. A specified bookingId is the exam session we just
 * ensured — never fall back to another booking on the same practice slot.
 * That fallback joins a finished dry-run and shows SESSION_ALREADY_ENDED
 * until a reload refreshes the list.
 */
export const resolveSlotMeetingBooking = (
  bookings: Booking[],
  opts: { slotId: string; bookingId?: string },
): Booking | undefined => {
  if (opts.bookingId) {
    return bookings.find(
      (booking) => booking.bookingId === opts.bookingId && booking.status !== 'cancelled',
    );
  }
  return selectCurrentBookingForSlot(bookings, opts.slotId);
};

interface UseEventTaskSlotsOptions {
  enabled?: boolean;
  pollSlots?: boolean;
  pollBookings?: boolean;
  /** Override exam session poll interval in ms (default 15000 when pollBookings is true) */
  examSessionPollMs?: number;
  /** Task payload for oral-exam completed-booking anchoring (official oral exams only). */
  task?: OralExamAnchorTask;
}

/**
 * The most recently cancelled registration for a session that has not started
 * yet. Past cancelled sessions are history, not news for the student.
 */
export const selectCancelledUpcomingBookingForTask = (
  bookings: Booking[],
  taskId: string | undefined,
  now: Date = new Date(),
): Booking | undefined => {
  if (!taskId) return undefined;
  const cancelledAtMs = (b: Booking) => new Date(b.cancelledAt ?? b.startTime).getTime();
  return bookings
    .filter((b) => b.taskId === taskId && b.status === 'cancelled' && new Date(b.startTime) > now)
    .sort((a, b) => cancelledAtMs(b) - cancelledAtMs(a))[0];
};

/**
 * useEventTaskSlots
 * -----------------
 * Fetches available slots for a given event task and handles booking / cancellation.
 * Includes polling for real-time updates.
 */
export const useEventTaskSlots = (
  courseId: string | undefined,
  moduleId: string | undefined,
  moduleVersion: string | undefined,
  taskId: string | undefined,
  options: UseEventTaskSlotsOptions = {},
) => {
  const {
    enabled = true, pollSlots = true, pollBookings = true, examSessionPollMs, task: anchorTask,
  } = options;

  const queryClient = useQueryClient();
  const user = useAuthStore((s) => s.user);
  const userId = user?.id || '';
  const refetchModuleTasks = useRefetchModuleTasks(courseId || '', moduleId || '');
  const isPreviewSourceActive = useStudentPreviewSource();
  const previewBookingState = useStudentPreviewBookingState();
  const { showToastMessage } = useDialog();
  const { t } = useTranslation();

  const baseEnabled = enabled && !!(courseId && moduleId && taskId);
  const queriesEnabled = baseEnabled && !isPreviewSourceActive;

  // User booking for this task - using useQuery directly to avoid parameter leakage
  const bookingsQueryKey = getGetUserBookingsQueryKey(userId, undefined);
  const { data: bookingsResp, isPending: bookingsLoading } = useQuery({
    queryKey: bookingsQueryKey,
    queryFn: ({ signal }) => getUserBookings(userId, undefined, { signal }),
    enabled: queriesEnabled && !!userId,
    staleTime: 30 * 1000, // 30 seconds for faster booking status updates
    refetchInterval: pollBookings ? 30 * 1000 : false, // 30s base interval
    refetchIntervalInBackground: false, // No background polling needed
  });

  const bookingList = (bookingsResp?.data?.data || []) as Booking[];
  const completedBookingAnchor = oralExamCompletedBookingAnchor(anchorTask);
  const currentBooking = selectCurrentBookingForTask(bookingList, taskId, completedBookingAnchor);

  // Available slots polling - normal frequency for slot availability
  const {
    data: slotsResp,
    isPending: slotsLoading,
    error: slotsError,
    queryKey: slotsQueryKey,
  } = useGetAvailableAppointmentSlots(courseId as string, moduleId as string, taskId as string, {
    query: {
      enabled: queriesEnabled,
      staleTime: 5 * 60 * 1000, // 5 minutes - slots change less frequently
      refetchInterval: pollSlots ? 2 * 60 * 1000 : false, // 2min when enabled
      refetchIntervalInBackground: false, // No background polling needed
    },
  });

  const slots = (slotsResp?.data?.data || []) as AppointmentSlot[];

  const finalCurrentBooking = currentBooking;

  // Find the corresponding slot for the current booking to get meeting details
  const currentSlot = finalCurrentBooking && slots.find((slot) => slot.slotId === finalCurrentBooking.slotId);
  const examSlotId = finalCurrentBooking?.slotId || '';
  const examPollInterval = examSessionPollMs ?? (pollBookings ? 15_000 : false);
  const { data: examSessionResp } = useGetExamSessionStatus(examSlotId, {
    query: {
      enabled: queriesEnabled && Boolean(examSlotId),
      refetchInterval: examPollInterval,
      refetchIntervalInBackground: false,
    },
  });

  // Mutations
  const createBookingMutation = useCreateBooking({
    mutation: {
      onSuccess: async () => {
        queryClient.invalidateQueries({ queryKey: slotsQueryKey });
        queryClient.invalidateQueries({ queryKey: bookingsQueryKey });
        queryClient.refetchQueries({ queryKey: bookingsQueryKey });
        queryClient.refetchQueries({ queryKey: slotsQueryKey });
        if (courseId && moduleId) {
          await refetchModuleTasks();
        }
      },
      onError: (error) => {
        log.warn('Booking failed:', error);
      },
    },
  });

  const cancelBookingMutation = useCancelBooking({
    mutation: {
      onSuccess: async () => {
        queryClient.invalidateQueries({ queryKey: slotsQueryKey });
        queryClient.invalidateQueries({ queryKey: bookingsQueryKey });
        queryClient.refetchQueries({ queryKey: bookingsQueryKey });
        queryClient.refetchQueries({ queryKey: slotsQueryKey });
        if (courseId && moduleId) {
          await refetchModuleTasks();
        }
      },
      onError: (error) => {
        log.warn('Cancellation failed:', error);
      },
    },
  });

  const previewToastMessage = t('lecturer.preview.studentActionUnavailable');

  const bookSlot = (slotId: string, consentGiven = false) => {
    if (isPreviewSourceActive) {
      guardStudentAction(isPreviewSourceActive, showToastMessage, previewToastMessage, () => undefined);
      return Promise.resolve(undefined);
    }
    return createBookingMutation.mutateAsync({
      courseId: courseId as string,
      moduleId: moduleId as string,
      taskId: taskId as string,
      data: { slotId, consentGiven },
    });
  };

  const cancelBooking = (bookingId: string) => {
    if (isPreviewSourceActive) {
      guardStudentAction(isPreviewSourceActive, showToastMessage, previewToastMessage, () => undefined);
      return Promise.resolve(undefined);
    }
    return cancelBookingMutation.mutateAsync({ userId, bookingId });
  };

  // Prefer booking's join URL if present, fall back to slot's join URL
  const bookingJoinUrl = finalCurrentBooking?.videoMeetingJoinUrl || null;
  const effectiveMeetingUrl = bookingJoinUrl || currentSlot?.videoMeetingJoinUrl || null;

  // Memo on stable scalar IDs only — finalCurrentBooking and currentSlot are new
  // object references on every react-query refetch, which would recreate the handle
  // and re-trigger the token request on every poll interval.
  const finalBookingId = finalCurrentBooking?.bookingId;
  const isLiveKit = isLiveKitSlot(currentSlot);
  const liveKitJoin: LiveKitJoinHandle | null = useMemo(() => {
    if (isPreviewSourceActive || !baseEnabled || !finalBookingId || !isLiveKit) return null;
    return createLiveKitJoinHandle({
      courseId: courseId as string,
      moduleId: moduleId as string,
      taskId: taskId as string,
      bookingId: finalBookingId,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [baseEnabled, courseId, isPreviewSourceActive, moduleId, taskId, finalBookingId, isLiveKit]);

  if (isPreviewSourceActive) {
    const previewSlots = previewBookingState?.slots ?? STUDENT_PREVIEW_SAMPLE_SLOTS;
    const previewBooking = selectCurrentBookingForTask(
      previewBookingState?.bookings ?? [],
      taskId,
      completedBookingAnchor,
    ) ?? null;
    return {
      slots: previewSlots,
      slotsLoading: false,
      slotsError: null,
      currentBooking: previewBooking,
      cancelledBooking: previewBooking
        ? null
        : selectCancelledUpcomingBookingForTask(previewBookingState?.bookings ?? [], taskId) ?? null,
      currentSlot: previewBooking ? previewSlots.find((slot) => slot.slotId === previewBooking.slotId) : undefined,
      examSessionStatus: previewBookingState?.examSessionStatus ?? null,
      meetingUrl: previewBooking?.videoMeetingJoinUrl ?? null,
      liveKitJoin: null,
      bookSlot,
      cancelBooking,
      bookingPending: false,
    };
  }

  return {
    slots,
    slotsLoading: slotsLoading || bookingsLoading,
    slotsError,
    currentBooking: finalCurrentBooking,
    cancelledBooking: finalCurrentBooking
      ? null
      : selectCancelledUpcomingBookingForTask(bookingList, taskId) ?? null,
    currentSlot,
    examSessionStatus: examSessionResp?.data?.data || null,
    meetingUrl: effectiveMeetingUrl,
    liveKitJoin,
    bookSlot,
    cancelBooking,
    bookingPending: createBookingMutation.isPending || cancelBookingMutation.isPending,
  };
};
